import { PriceLigne, OptionsType, FlexyButton } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import {
    specificsArticleLabels,
    specificsArticleLibelles,
    useFormaliteService,
    useOffreService,
} from '@europrocurement/l2d-domain';
import { Box, Divider, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    PrestationTarifDTODonneesFacturationRead,
    TauxTvaDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';

import { PrestationArticlesJsonldPrestationrelationCreate } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useToaster } from '@europrocurement/l2d-modules/hooks';
import { useForm } from 'react-hook-form';
import { calculTotalHt, calculTotalTtc, calculTotalTva } from '@europrocurement/l2d-utils';
import {
    ArticlesJsonldArticleLibRead,
    ArticlesJsonldArticleRead,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { ArticlePrefacturation } from '@europrocurement/l2d-modules/providers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/sharp-regular-svg-icons';

export type ArticleAddProps = {
    prestations: PrestationTarifDTODonneesFacturationRead[];
    tauxTva: TauxTvaDTOJsonldDonneesFacturationRead[];
    afterSubmit: () => void;
    onCreate?: (article: ArticlePrefacturation) => void;
};

const formatArticleOptionLabel = (
    option: unknown,
    meta: { context: 'menu' | 'value' },
): React.ReactNode => {
    const { label, catArticleLabel } = option as {
        label: string;
        catArticleLabel?: string | null;
    };
    if (meta.context === 'menu' && catArticleLabel) {
        return (
            <Typography
                component="span"
                sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}
            >
                <Typography
                    component="span"
                    sx={{ flex: 1 }}
                >
                    {label}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        px: '6px',
                        py: '2px',
                        borderRadius: '10px',
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        fontSize: '0.65rem',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    <FontAwesomeIcon
                        icon={faCircleInfo}
                        size="xs"
                    />
                    {catArticleLabel}
                </Typography>
            </Typography>
        );
    }
    return <Typography component="span">{label}</Typography>;
};

const ArticleAdd: React.FunctionComponent<ArticleAddProps> = function (props) {
    const { afterSubmit, onCreate, prestations, tauxTva } = props;

    const { prestationArticlesApi } = useFormaliteService();
    const { articlesApi } = useOffreService();
    const [articles, setArticles] = useState<Array<ArticlesJsonldArticleRead>>([]);
    const [articlesLib, setArticlesLib] = useState<Array<ArticlesJsonldArticleLibRead>>([]);

    const CorrectLibelle = (libelle: string | null | undefined) => {
        switch (libelle) {
            case specificsArticleLabels.lineBreak:
                return specificsArticleLibelles.lineBreak;
            case specificsArticleLabels.section:
                return specificsArticleLibelles.section;
            default:
                return libelle || ('' as string);
        }
    };
    const CorrectLibelleComplete = (
        article: ArticlesJsonldArticleRead | ArticlesJsonldArticleLibRead,
    ) => {
        if ('codeArticle' in article && article.codeArticle) {
            return CorrectLibelle(`${article.libelle} (${article.codeArticle})`);
        }
        return CorrectLibelle(article.libelle);
    };

    useEffect(() => {
        articlesApi
            .apiArticleLibGet()
            .then((res) => setArticlesLib(res.data['hydra:member']))
            .catch((err) => console.log(err));

        articlesApi
            .apiArticlesGetCollection({
                domaineId: 3,
                excludeForSelect: true,
                itemsPerPage: 70,
            })
            .then((res) => {
                const filteredArticles = res.data['hydra:member'].filter(
                    (article: ArticlesJsonldArticleRead) =>
                        article.rubArticle !== 'DIV' || article.cegidCompte,
                );
                setArticles(filteredArticles);
            })
            .catch((err) => console.log(err));
    }, [articlesApi]);

    const sortAlpha = (subset: ArticlesJsonldArticleRead[]) =>
        [...subset].sort((a, b) =>
            CorrectLibelleComplete(a).localeCompare(CorrectLibelleComplete(b), 'fr', {
                sensitivity: 'base',
            }),
        );

    const isValidArticle = (a: ArticlesJsonldArticleRead) =>
        typeof a.libelle === 'string' && typeof a.id === 'number';

    const groupByRubrique = (subset: ArticlesJsonldArticleRead[]) =>
        Object.values(
            subset.reduce(
                (acc, article) => {
                    const key = article.rubArticle || 'Autres';
                    if (!acc[key]) acc[key] = { group: key, articles: [] };
                    acc[key].articles.push(article);
                    return acc;
                },
                {} as Record<string, { group: string; articles: ArticlesJsonldArticleRead[] }>,
            ),
        )
            .map((g) => ({ ...g, articles: sortAlpha(g.articles) }))
            .sort((a, b) => a.group.localeCompare(b.group, 'fr', { sensitivity: 'base' }));

    // Section "fréquemment utilisés" : rang=10 uniquement, aplati (rubrique dans le label)
    const topArticles = sortAlpha(articles.filter((a) => a.rang === 10));

    // Section "Autres" : tout ce qui n'est pas rang=10, groupé par rubrique
    const otherGroups = groupByRubrique(articles.filter((a) => (a.rang ?? null) !== 10));

    // Flatten for select options (if needed elsewhere)
    const allArticles = [...articlesLib, ...articles];

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            qte: 1,
            ht: 0,
            libelle: '',
            bold: false,
            idPresta: { label: prestations[0].libelle, value: prestations[0].id },
            factArticle: { label: undefined, value: undefined },
        },
    });

    const selectedFactArticle = allArticles.find(
        (item) => item.id === Number(formContext.getValues('factArticle')?.value),
    );

    useEffect(() => {
        if (selectedFactArticle) {
            if (
                selectedFactArticle.libelle === specificsArticleLabels.lineBreak ||
                selectedFactArticle.libelle === specificsArticleLabels.section
            ) {
                formContext.setValue('libelle', '');
            } else if ('libelle' in selectedFactArticle) {
                formContext.setValue(
                    'libelle',
                    selectedFactArticle.libelle ? selectedFactArticle.libelle : '',
                );
            }
            formContext.setValue(
                'ht',
                selectedFactArticle && 'prixUnitaire' in selectedFactArticle
                    ? Number(selectedFactArticle.prixUnitaire)
                    : 0,
            );
        }
    }, [formContext, selectedFactArticle]);

    const selectedFactArticleTvaValue =
        selectedFactArticle && 'natureTva' in selectedFactArticle
            ? selectedFactArticle.natureTva
            : 'NS';
    const selectedFactArticleRubFacValue =
        selectedFactArticle && 'rubArticle' in selectedFactArticle
            ? selectedFactArticle.rubArticle
            : 'LIB';

    const tvaValue = tauxTva.find(
        (itemTva) => itemTva.codeTva === selectedFactArticleTvaValue,
    )?.tauxTva;

    const formStructure: FormStructure[] = [
        {
            type: 'header',
            label: 'Ajouter une prestation',
            name: 'main_informations',
        },
        {
            type: 'select',
            name: 'factArticle',
            inputlabel: 'Article',
            options: [
                {
                    label: 'Libellé',
                    options: articlesLib
                        .filter((a) => typeof a.libelle === 'string' && typeof a.id === 'number')
                        .map((a) => ({ label: CorrectLibelleComplete(a), value: a.id as number })),
                },
                ...(topArticles.length > 0
                    ? [
                          {
                              label: 'Articles fréquemment utilisés',
                              options: topArticles.filter(isValidArticle).map((a) => ({
                                  label: `[${a.rubArticle || 'Autres'}] ${CorrectLibelleComplete(a)}`,
                                  value: a.id as number,
                                  catArticleLabel: a.catArticle?.libelle ?? null,
                              })),
                          },
                      ]
                    : []),
                ...otherGroups.map((group) => ({
                    label: group.group,
                    options: group.articles.filter(isValidArticle).map((a) => ({
                        label: CorrectLibelleComplete(a),
                        value: a.id as number,
                        catArticleLabel: a.catArticle?.libelle ?? null,
                    })),
                })),
            ] as unknown as OptionsType,
            formatOptionLabel: formatArticleOptionLabel,
            placeholder: "Recherche d'articles (Libellé, code article ...)",
            rules: {
                required: 'Vous devez sélectionner une prestation',
            },
            xs: 12,
            sm: 12,
            md: 4,
            lg: 4,
        },
        {
            type: 'text',
            name: 'libelle',
            inputlabel: 'Libellé',
            rules: {
                required: 'Vous devez renseigner un libellé',
            },
            placeholder: 'Libellé de la prestation',
            xs: 12,
            sm: 6,
            md: 4,
            lg: 4,
            disabled:
                selectedFactArticle &&
                selectedFactArticle?.libelle === specificsArticleLabels.lineBreak,
        },
        {
            type: 'select',
            name: 'idPresta',
            inputlabel: 'Démarche liée',
            options: prestations
                .filter((prestation) => prestation.id !== undefined)
                .map((prestation) => ({
                    label: prestation.libelle,
                    value: prestation.id,
                })) as OptionsType,
            placeholder: 'Veuillez sélectionner une démarche',
            rules: {
                required: 'Vous devez sélectionner une démarche',
            },
            xs: 12,
            sm: 6,
            md: 4,
            lg: 4,
        },
    ];
    const formStructureConditioned: FormStructure[] = [
        {
            type: 'number',
            inputProps: {
                step: 1,
                min: 0,
            },
            name: 'ht',
            inputlabel: 'Prix unitaire',
            placeholder: 'Prix unitaire',
            rules: {
                validate: (value: number) => {
                    if (selectedFactArticleRubFacValue !== 'LIB') {
                        return true;
                    }
                    return value ? true : 'Vous devez reseigner un prix';
                },
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                if (Number(e.target.value) === 0) {
                    // eslint-disable-next-line no-param-reassign
                    e.target.value = '';
                }
            },
            xs: 12,
            sm: 4,
            md: 4,
            lg: 4,
        },
        {
            type: 'number',
            inputProps: {
                step: 1,
                min: 0,
            },
            name: 'qte',
            inputlabel: 'Quantité',
            placeholder: 'Quantité',
            rules: {
                validate: (value: number) => {
                    if (selectedFactArticleRubFacValue !== 'LIB') {
                        return true;
                    }
                    return value ? true : 'Vous devez renseigner une quantité';
                },
            },
            xs: 12,
            sm: 4,
            md: 4,
            lg: 4,
        },
        {
            type: 'number',
            disabled: true,
            inputlabel: '% TVA',
            name: 'tva',
            defaultValue: tvaValue,
            rules: {
                required: 'Selectionner une prestation pour définir une TVA',
            },
            xs: 12,
            sm: 4,
            md: 4,
            lg: 4,
        },
    ];
    if (selectedFactArticleRubFacValue !== 'LIB') {
        formStructure.push(...formStructureConditioned);
    }
    const { handleApiResponse } = useToaster();

    const onSubmit = async () => {
        let libelle = formContext.getValues().libelle as unknown as string;
        if (
            selectedFactArticle &&
            selectedFactArticle?.libelle === specificsArticleLabels.lineBreak
        ) {
            libelle = '';
        }

        let bold = false;
        let tarif = '0';
        let quantite = 0;
        const prestaId = formContext.getValues().idPresta?.value as unknown as string;

        if (selectedFactArticleRubFacValue !== 'LIB') {
            tarif =
                formContext.getValues().ht === 0
                    ? '0'
                    : (formContext.getValues().ht as unknown as string);
            quantite = Number(formContext.getValues().qte as unknown as number);
        }
        bold = formContext.getValues().bold;
        const prestationArticlesJsonldPrestationrelationCreate: PrestationArticlesJsonldPrestationrelationCreate =
            {
                idPresta: prestaId,
                libelle,
                quantite,
                factArticle: formContext.getValues().factArticle.value as unknown as string,
                tarif: String(tarif),
                bold,
            };

        const createRequest = prestationArticlesApi.apiArticlesPrestationPost({
            prestationArticlesJsonldPrestationrelationCreate,
        });

        if (onCreate) {
            onCreate({
                id: 1,
                quantite,
                libelle,
                prixBase: tarif,
                rubFac: selectedFactArticleRubFacValue as string,
                tauxTva: tvaValue as string,
                ttc: calculTotalTtc(tarif, quantite, tvaValue).toFixed(2) as unknown as number,
                rang: 9999,
                prestationId: prestaId as unknown as number,
                articleFacturable: true,
                bold: false,
            });
        }

        await handleApiResponse(createRequest, afterSubmit);
    };

    const formProps = {
        formContext,
        onSubmit,
        formObject: {},
        submitButton: {
            displayed: false,
        },
    };

    formContext.watch(['factArticle']);
    formContext.watch(['ht']);
    formContext.watch(['qte']);
    return (
        <>
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />

            {selectedFactArticleRubFacValue !== 'LIB' ? (
                <>
                    <Divider
                        sx={{
                            marginTop: '10px',
                        }}
                    />
                    <PriceLigne
                        label="Total HT"
                        price={`${calculTotalHt(formContext.getValues(`ht`), formContext.getValues(`qte`)).toFixed(2)} €`}
                        sx={{
                            marginTop: '10px',
                        }}
                    />
                    <PriceLigne
                        label=" TVA"
                        price={`${calculTotalTva(formContext.getValues(`ht`), formContext.getValues(`qte`), tvaValue).toFixed(2)} €`}
                    />
                    <PriceLigne
                        label=" Total TTC"
                        price={`${calculTotalTtc(formContext.getValues(`ht`), formContext.getValues(`qte`), tvaValue).toFixed(2)} €`}
                    />
                </>
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                    gap: '32px',
                    marginTop: '10px',
                }}
            >
                <FlexyButton
                    type="submit"
                    variant="contained"
                    sx={{ padding: '16px 24px' }}
                    label="Valider"
                    onClick={onSubmit}
                    disabled={
                        !formContext.getValues().factArticle ||
                        formContext.getValues().factArticle.value === undefined
                    }
                />
            </Box>
        </>
    );
};

export default ArticleAdd;
